import type {
    ColorPickerContrastConstantType,
    ColorPickerPresetsConstantType,
    ColorPickerFieldsConstantType,
    ColorPickerParseConstantType,
    ColorPickerModelConstantType,
    ColorPickerHexConstantType,
    ColorPickerEyeDropperType,
    ColorPickerContrast,
    ColorPickerFormat,
    ColorPickerModel,
    ColorPickerField
} from "./type";
import type {
    ColorSwatchPickerItem
} from "../colorSwatchPicker/type";
import {
    getContrastRatio,
    parseColorString,
    getApcaContrast,
    type ColorVector,
    formatColorHex,
    compositeOver,
    getLuminance,
    rgbToOklch,
    oklchToHex,
    toChannels,
    formatHex
} from "../../helpers/color";
import {
    formatPercent,
    formatNumber
} from "../../helpers/locale";

export const COLOR_PICKER_DEFAULT_FORMATS: Array<ColorPickerFormat> = [
    "hex",
    "rgb",
    "oklch"
];

export const COLOR_PICKER_DEFAULT_VALUE = "#006155";

export const COLOR_PICKER_AREA_ASPECT_RATIO = 1.6;

export const COLOR_PICKER_FORMAT_LABELS: Record<ColorPickerFormat, string> = {
    oklch: "OKLCH",
    hex: "HEX",
    rgb: "RGB"
};

export const COLOR_PICKER_PRESET_KEYS: Array<keyof NCoreUIKit.Translation> = [
    "colorPicker-presetTeal",
    "colorPicker-presetBrick",
    "colorPicker-presetBlue",
    "colorPicker-presetMustard",
    "colorPicker-presetPurple",
    "colorPicker-presetGreen",
    "colorPicker-presetCyan",
    "colorPicker-presetRose"
];

export const COLOR_PICKER_CONTRAST_TARGET = 4.5;

export const COLOR_PICKER_CONTRAST_LARGE_TARGET = 3;

export const COLOR_PICKER_APCA_TEXT_LEVEL = 60;

export const COLOR_PICKER_NEUTRAL_CHROMA = 0.004;

export const COLOR_PICKER_ECHO_LIMIT = 8;

const MID_GREY: ColorVector = [
    0.5,
    0.5,
    0.5
];

const CHANNEL_MAX = 255;

const PERCENT_MAX = 100;

const HUE_MAX = 360;

const CHROMA_MAX = 0.5;

const FIELD_FLEX_WIDE = 2;

export const getColorPickerRgb = (model: ColorPickerModel): ColorVector => {
    return model.rgb ?? parseColorString(oklchToHex(model.l, model.c, model.h))?.rgb ?? [
        0,
        0,
        0
    ];
};

export const getColorPickerHex = ({
    isAlphaEnabled,
    model
}: ColorPickerHexConstantType) => {
    return formatColorHex({
        rgb: getColorPickerRgb(model),
        alpha: model.alpha,
        isAlphaEnabled
    });
};

export const getColorPickerModel = ({
    isAlphaEnabled,
    value,
    hue
}: ColorPickerModelConstantType): ColorPickerModel | null => {
    const parsed = parseColorString(value);

    if(!parsed) {
        return null;
    }

    const [
        l,
        c,
        h
    ] = rgbToOklch(parsed.rgb);

    return {
        h: (c as number) < COLOR_PICKER_NEUTRAL_CHROMA ? hue : h as number,
        alpha: isAlphaEnabled ? parsed.alpha : 1,
        rgb: parsed.rgb,
        c: c as number,
        l: l as number
    };
};

export const getColorPickerEyeDropper = (): ColorPickerEyeDropperType | undefined => {
    return (globalThis as {
        EyeDropper?: ColorPickerEyeDropperType;
    }).EyeDropper;
};

export const getColorPickerFields = ({
    isAlphaEnabled,
    activeLocale,
    format,
    model,
    rgb
}: ColorPickerFieldsConstantType): Array<ColorPickerField> => {
    const alphaField: Array<ColorPickerField> = isAlphaEnabled ? [
        {
            text: formatPercent({
                options: {
                    maximumFractionDigits: 0
                },
                locale: activeLocale,
                value: model.alpha
            }),
            label: "colorSlider-alpha",
            key: "alpha",
            flex: 1
        }
    ] : [];

    if(format === "hex") {
        return [
            {
                text: formatHex(rgb).slice(1),
                label: "colorPicker-hex",
                flex: FIELD_FLEX_WIDE,
                key: "hex"
            },
            ...alphaField
        ];
    }

    if(format === "rgb") {
        const channels = toChannels(rgb);

        return [
            {
                text: `${Math.round(channels[0])}`,
                label: "colorPicker-channelRed",
                key: "red",
                flex: 1
            },
            {
                text: `${Math.round(channels[1])}`,
                label: "colorPicker-channelGreen",
                key: "green",
                flex: 1
            },
            {
                text: `${Math.round(channels[2])}`,
                label: "colorPicker-channelBlue",
                key: "blue",
                flex: 1
            },
            ...alphaField
        ];
    }

    return [
        {
            text: formatPercent({
                options: {
                    maximumFractionDigits: 0
                },
                locale: activeLocale,
                value: model.l
            }),
            label: "colorSlider-lightness",
            key: "lightness",
            flex: 1
        },
        {
            text: formatNumber({
                options: {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3
                },
                locale: activeLocale,
                value: model.c
            }),
            label: "colorSlider-chroma",
            key: "chroma",
            flex: 1
        },
        {
            text: `${Math.round(model.h)}°`,
            label: "colorSlider-hue",
            key: "hue",
            flex: 1
        },
        ...alphaField
    ];
};

const getFieldNumber = (text: string): number | null => {
    const cleaned = text.replace(/[%°\s]/g, "").replace(",", ".");

    if(cleaned.length === 0) {
        return null;
    }

    const value = Number(cleaned);

    return Number.isFinite(value) ? value : null;
};

const getModelFromRgb = ({
    isAlphaEnabled,
    model,
    rgb
}: {
    isAlphaEnabled: boolean;
    model: ColorPickerModel;
    rgb: ColorVector;
}): ColorPickerModel => {
    const [
        l,
        c,
        h
    ] = rgbToOklch(rgb);

    return {
        h: (c as number) < COLOR_PICKER_NEUTRAL_CHROMA ? model.h : h as number,
        alpha: isAlphaEnabled ? model.alpha : 1,
        c: c as number,
        l: l as number,
        rgb
    };
};

export const parseColorPickerField = ({
    isAlphaEnabled,
    model,
    text,
    key,
    rgb
}: ColorPickerParseConstantType): ColorPickerModel | null => {
    if(key === "hex") {
        const stripped = text.trim().replace(/^#/, "");

        const parsed = parseColorString(/^[0-9a-f]+$/i.test(stripped) ? `#${stripped}` : text);

        if(!parsed) {
            return null;
        }

        return getModelFromRgb({
            model: {
                ...model,
                alpha: parsed.alpha
            },
            isAlphaEnabled,
            rgb: parsed.rgb
        });
    }

    const value = getFieldNumber(text);

    if(value === null) {
        return null;
    }

    if(key === "alpha") {
        return value < 0 || value > PERCENT_MAX ? null : {
            ...model,
            alpha: value / PERCENT_MAX
        };
    }

    if(key === "red" || key === "green" || key === "blue") {
        const channel = Math.round(value);

        if(channel < 0 || channel > CHANNEL_MAX) {
            return null;
        }

        const index = key === "red" ? 0 : key === "green" ? 1 : 2;

        const next: ColorVector = [
            ...toChannels(rgb).map((entry) => Math.round(entry) / CHANNEL_MAX)
        ] as ColorVector;

        next[index] = channel / CHANNEL_MAX;

        return getModelFromRgb({
            isAlphaEnabled,
            rgb: next,
            model
        });
    }

    if(key === "lightness") {
        return value < 0 || value > PERCENT_MAX ? null : {
            ...model,
            l: value / PERCENT_MAX,
            rgb: null
        };
    }

    if(key === "chroma") {
        return value < 0 || value > CHROMA_MAX ? null : {
            ...model,
            rgb: null,
            c: value
        };
    }

    return value < 0 || value > HUE_MAX ? null : {
        ...model,
        h: value % HUE_MAX,
        rgb: null
    };
};

export const getColorPickerContrast = ({
    surface,
    model
}: ColorPickerContrastConstantType): ColorPickerContrast => {
    const foreground = compositeOver({
        foreground: getColorPickerRgb(model),
        background: surface,
        alpha: model.alpha
    });

    const ratio = getContrastRatio(foreground, surface);

    const level = Math.abs(getApcaContrast({
        background: surface,
        text: foreground
    }));

    const isDark = getLuminance(surface) < getLuminance(MID_GREY);

    return {
        isGood: ratio >= COLOR_PICKER_CONTRAST_TARGET && (level >= COLOR_PICKER_APCA_TEXT_LEVEL || isDark),
        isDark,
        ratio,
        level
    };
};

export const getColorPickerPresets = ({
    localize,
    colors
}: ColorPickerPresetsConstantType): Array<ColorSwatchPickerItem> => {
    return COLOR_PICKER_PRESET_KEYS.map((key, index) => ({
        color: colors.chart.categorical[`${index + 1}` as keyof typeof colors.chart.categorical],
        title: localize(key)
    }));
};
